import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    TextField, Button, Switch, FormControlLabel, Chip, Alert,
    CircularProgress, Divider, Tabs, Tab, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SectionTitle from './SectionTitle';
import api from '../../services/api';

const TIPOS = [
    {
        key: 'ACEITE_DOCUMENTO',
        label: 'Aceite de Documento',
        desc: 'Enviada quando um documento aguarda aceite eletrônico do paciente.',
        vars: ['{nome}', '{numeroDocumento}', '{link}'],
    },
    {
        key: 'CONTRATO',
        label: 'Contrato',
        desc: 'Enviada quando um contrato de prestação de serviços é gerado para o paciente.',
        vars: ['{nome}', '{numeroDocumento}', '{link}'],
    },
    {
        key: 'RENOVACAO_ASSINATURA',
        label: 'Renovação de Assinatura',
        desc: 'Enviada automaticamente nos dias de renovação configurados.',
        vars: ['{nome}', '{periodo}', '{link}'],
    },
    {
        key: 'GENERICO',
        label: 'Genérico',
        desc: 'Usado para testes e notificações avulsas.',
        vars: ['{assunto}', '{corpo}', '{nome}', '{link}', '{extra}'],
    },
];

const CANAL_TABS = ['EMAIL', 'WHATSAPP'];
const CANAL_LABEL = { EMAIL: 'E-mail', WHATSAPP: 'WhatsApp' };

function VariaveisChip({ vars }) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {vars.map(v => (
                <Tooltip key={v} title="Clique para copiar">
                    <Chip
                        size="small"
                        label={v}
                        onClick={() => navigator.clipboard?.writeText(v)}
                        sx={{ fontFamily: 'monospace', cursor: 'pointer', fontSize: 11 }}
                    />
                </Tooltip>
            ))}
        </Box>
    );
}

function TemplateEditor({ tipo, canal, templateInicial, onSaved }) {
    const [dados, setDados] = useState({
        assunto: templateInicial?.assunto ?? '',
        corpo: templateInicial?.corpo ?? '',
        ativo: templateInicial?.ativo ?? true,
    });
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    useEffect(() => {
        setDados({
            assunto: templateInicial?.assunto ?? '',
            corpo: templateInicial?.corpo ?? '',
            ativo: templateInicial?.ativo ?? true,
        });
    }, [templateInicial]);

    const salvar = async () => {
        setSaving(true);
        setMsg(null);
        try {
            const res = await api.put(`/templates-notificacao/${tipo}/${canal}`, dados);
            setMsg({ tipo: 'success', texto: 'Template salvo.' });
            onSaved?.(res.data);
        } catch {
            setMsg({ tipo: 'error', texto: 'Erro ao salvar template.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
                control={
                    <Switch
                        checked={dados.ativo}
                        onChange={e => setDados(s => ({ ...s, ativo: e.target.checked }))}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#1ABB9C' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1ABB9C' },
                        }}
                    />
                }
                label={dados.ativo ? 'Usando este template' : 'Usando template padrão do sistema'}
            />

            {canal === 'EMAIL' && (
                <TextField
                    fullWidth
                    size="small"
                    label="Assunto"
                    value={dados.assunto}
                    onChange={e => setDados(s => ({ ...s, assunto: e.target.value }))}
                    disabled={!dados.ativo}
                    placeholder="Ex: Documento aguardando aceite — {numeroDocumento}"
                />
            )}

            <TextField
                fullWidth
                multiline
                minRows={5}
                maxRows={12}
                label="Corpo da mensagem"
                value={dados.corpo}
                onChange={e => setDados(s => ({ ...s, corpo: e.target.value }))}
                disabled={!dados.ativo}
                placeholder={canal === 'WHATSAPP'
                    ? 'Olá, {nome}!\n\nSeu documento está disponível:\n{link}'
                    : 'Olá, {nome}!\n\nClique no link para acessar:\n{link}'}
                helperText={!dados.ativo ? 'Ative para personalizar.' : undefined}
            />

            {msg && <Alert severity={msg.tipo} sx={{ py: 0 }}>{msg.texto}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                    onClick={salvar}
                    disabled={saving}
                    sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}
                >
                    {saving ? 'Salvando...' : 'Salvar template'}
                </Button>
            </Box>
        </Box>
    );
}

export default function SecaoMensagens() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [canalAtivo, setCanalAtivo] = useState(0);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/templates-notificacao');
            setTemplates(res.data ?? []);
        } catch {
            // ignora — fallbacks inline serão usados
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const encontrar = (tipo, canal) =>
        templates.find(t => t.tipo === tipo && t.canal === canal) ?? null;

    const atualizar = (novo) =>
        setTemplates(prev => {
            const idx = prev.findIndex(t => t.tipo === novo.tipo && t.canal === novo.canal);
            return idx >= 0
                ? prev.map((t, i) => (i === idx ? novo : t))
                : [...prev, novo];
        });

    const canal = CANAL_TABS[canalAtivo];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box>
            <SectionTitle descricao="Personalize o conteúdo enviado em cada situação. Deixe inativo para usar o texto padrão do sistema.">
                Templates de Mensagem
            </SectionTitle>

            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
                Use as variáveis abaixo dentro do texto — elas serão substituídas automaticamente no envio.
            </Alert>

            <Tabs
                value={canalAtivo}
                onChange={(_, v) => setCanalAtivo(v)}
                sx={{
                    mb: 3,
                    '& .Mui-selected': { color: '#1ABB9C !important' },
                    '& .MuiTabs-indicator': { backgroundColor: '#1ABB9C' },
                }}
            >
                {CANAL_TABS.map(c => (
                    <Tab key={c} label={CANAL_LABEL[c]} sx={{ textTransform: 'none', fontWeight: 600 }} />
                ))}
            </Tabs>

            {TIPOS.map((tipo, idx) => (
                <Accordion key={tipo.key} defaultExpanded={idx === 0}
                    sx={{ mb: 1, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', '&:before': { display: 'none' }, borderRadius: '8px !important' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box>
                            <Typography fontWeight={600}>{tipo.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{tipo.desc}</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                Variáveis disponíveis:
                            </Typography>
                            <VariaveisChip vars={tipo.vars} />
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <TemplateEditor
                            tipo={tipo.key}
                            canal={canal}
                            templateInicial={encontrar(tipo.key, canal)}
                            onSaved={atualizar}
                        />
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
}
